import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import './App.css';

const scrollToSection = (event, sectionId) => {
    event.preventDefault();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(sectionId)?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
};

// Jump to the top before opening another page, so it doesn't open mid-scroll.
const jumpToTop = () => {
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    root.style.scrollBehavior = previousBehavior;
};

const App = () => {
    const [contactStatus, setContactStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleContactSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        const form = event.currentTarget;
        const formData = new FormData(form);
        const payload = Object.fromEntries(formData.entries());

        setIsSubmitting(true);
        setContactStatus({ type: '', message: '' });

        try {
            const response = await fetch('contact.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Your message could not be sent. Please try again.');
            }

            form.reset();
            setContactStatus({ type: 'success', message: result.message });
        } catch (error) {
            setContactStatus({
                type: 'error',
                message: error.message || 'Unable to reach the contact service. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div id="page-top">
            <Navbar />

            <main>
                <header className="home-hero">
                    <div className="container home-hero-content">
                        <div className="hero-copy">
                            <h1>Shaurya Kala</h1>
                            <p className="hero-intro">
                                Hi there! I'm a fullstack developer and computer science student in my senior year, looking for internships and early-career opportunities.
                            </p>

                            <div className="hero-tech-list" aria-label="Core technologies">
                                <span>CS Undergrad Senior</span>
                                <span>Graduating Dec 2026</span>
                                <span>2x AWS Certified</span>
                                <span>Python Google Certified</span>
                            </div>

                            <div className="hero-credentials">
                                <span>Java</span>
                                <span>React</span>
                                <span>Spring Boot</span>
                                <span>AWS</span>
                            </div>

                            <div className="hero-actions">
                                <a
                                    className="btn btn-primary btn-lg"
                                    href="#projects"
                                    onClick={(event) => scrollToSection(event, 'projects')}
                                >
                                    View my work
                                </a>
                                <a className="btn btn-outline-light btn-lg" href="/static/media/Shaurya_Kala_Resume.pdf">
                                    Resume
                                </a>
                            </div>

                            <div className="hero-social-links" aria-label="Social profiles">
                                <a
                                    href="https://www.linkedin.com/in/shauryakala"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    LinkedIn
                                </a>
                            </div>
                        </div>

                        <aside className="hero-card" aria-label="Full-stack technology overview">
                            <div className="hero-card-topline">
                                <span className="hero-card-indicator"></span>
                                JUNIOR FULL-STACK DEVELOPMENT
                            </div>
                            <div className="hero-card-layer">
                                <div>
                                    <span className="hero-card-label">Frontend</span>
                                    <strong>React, HTML/CSS/Javascript, JQuery</strong>
                                </div>
                            </div>
                            <div className="hero-card-layer">
                                <div>
                                    <span className="hero-card-label">Backend</span>
                                    <strong>Spring Boot, Microservices, MySQL, Firebase</strong>
                                </div>
                            </div>
                            <div className="hero-card-layer">
                                <div>
                                    <span className="hero-card-label">Tools</span>
                                    <strong>AWS, Docker, GitHub, REST APIs, Figma</strong>
                                </div>
                            </div>
                            <p className="hero-card-caption">
                                Other languages: Python, Lua
                            </p>
                        </aside>
                    </div>
                </header>

                <section className="page-section home-page-section projects-section" id="projects">
                    <div className="container">
                        <h2 className="page-section-heading text-center text-uppercase text-secondary mb-4">
                            Projects
                        </h2>
                        <div className="row g-4 justify-content-center">
                            <div className="col-md-6 col-lg-4">
                                <article className="achievement-card project-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="far fa-newspaper"></i>
                                        </div>
                                        <h3>Real-Time News Feed</h3>
                                        <p>
                                            Pulls the latest headlines from the NewsData.io REST API and shows them as cards, with loading, error, and refresh states.
                                        </p>
                                        <Link className="project-link stretched-link" to="/news" onClick={jumpToTop}>
                                            Read the news feed <span aria-hidden="true">&rarr;</span>
                                        </Link>
                                    </div>
                                </article>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <article className="achievement-card project-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="fas fa-comments"></i>
                                        </div>
                                        <h3>Real-Time Chat</h3>
                                        <p>
                                            Sign in with Google (SSO) and chat with other visitors. Messages appear instantly for everyone, powered by Firebase Authentication and Cloud Firestore.
                                        </p>
                                        <Link className="project-link stretched-link" to="/chat" onClick={jumpToTop}>
                                            Try the live chat <span aria-hidden="true">&rarr;</span>
                                        </Link>
                                    </div>
                                </article>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <article className="achievement-card project-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="fas fa-plane"></i>
                                        </div>
                                        <h3>Airline Booking Platform</h3>
                                        <p>
                                            A flight booking system I'm building with Spring Boot microservices, an API gateway, Kafka events, and Razorpay payments. Still in progress.
                                        </p>
                                        <Link className="project-link stretched-link" to="/airline" onClick={jumpToTop}>
                                            See the architecture <span aria-hidden="true">&rarr;</span>
                                        </Link>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="page-section home-page-section website-about-section" id="about-this-site">
                    <div className="container">
                        <div className="website-about-copy">
                            <h2 className="page-section-heading text-center text-uppercase text-secondary mb-4">
                                About this website
                            </h2>
                            <p className="website-about-intro">
                                I built this site as a place to showcase my skills and practice fullstack web developments using interconnected technologies.
                            </p>
                        </div>
                        <figure className="website-architecture-diagram">
                            <img
                                src="/static/media/shauryakala-architecture.png"
                                width="406"
                                height="388"
                                loading="lazy"
                                alt="Website Architecture Diagram"
                            />
                        </figure>
                        <div className="website-architecture">
                            <article>
                                <h3>Frontend</h3>
                                <p>
                                    I use <strong>ReactJS</strong> for the frontend, with <strong>React Router</strong> for navigation and <strong>Bootstrap</strong> for responsive layout. The site is a single-page application that fetches data from APIs and renders it dynamically.
                                </p>
                            </article>
                            <article>
                                <h3>Backend</h3>
                                <p>
                                    I use <strong>Firebase</strong> for authentication and <strong>Firestore</strong> for real-time data storage. The backend is <strong>serverless</strong>, with Firebase handling user authentication and data synchronization. I also use a small <strong>PHP</strong> endpoint for handling contact form submissions.
                                </p>
                            </article>
                            <article>
                                <h3>Other Tools</h3>
                                <p>
                                    I utilize <strong>REST APIs</strong> for fetching news articles and other data. The site is hosted on a cloud platform, ensuring scalability and reliability. I'm also using <strong>Git</strong> for version control and <strong>continuous integration</strong>.
                                </p>
                            </article>
                        </div>
                    </div>
                </section>

                <section className="page-section home-page-section portfolio" id="achievements">
                    <div className="container">
                        <h2 className="page-section-heading text-center text-uppercase text-secondary mb-4">
                            Achievements
                        </h2>
                        <div className="row g-4 justify-content-center">
                            <div className="col-md-6 col-xl-3">
                                <article className="achievement-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="fab fa-aws"></i>
                                        </div>
                                        <span className="achievement-label">AWS Certification</span>
                                        <h3>AWS Certified Solutions Architect – Associate</h3>
                                    </div>
                                </article>
                            </div>
                            <div className="col-md-6 col-xl-3">
                                <article className="achievement-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="fab fa-aws"></i>
                                        </div>
                                        <span className="achievement-label">AWS Certification</span>
                                        <h3>AWS Certified CloudOps Engineer – Associate</h3>
                                    </div>
                                </article>
                            </div>
                            <div className="col-md-6 col-xl-3">
                                <article className="achievement-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="fab fa-google"></i>
                                        </div>
                                        <span className="achievement-label">Google Certificate</span>
                                        <h3>Google IT Automation with Python</h3>
                                        <p>
                                            <a
                                                href="https://www.coursera.org/account/accomplishments/specialization/HT4GWUTJC3PU"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                View credential on Coursera
                                            </a>
                                        </p>
                                    </div>
                                </article>
                            </div>
                            <div className="col-md-6 col-xl-3">
                                <article className="achievement-card">
                                    <div className="achievement-card-copy">
                                        <div className="achievement-card-icon" aria-hidden="true">
                                            <i className="fas fa-award"></i>
                                        </div>
                                        <span className="achievement-label">Award</span>
                                        <h3>1st place at Nationals for Web Design</h3>
                                        <p>
                                            Placed first in a solo national US Web Design tournament.
                                        </p>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="page-section home-page-section about-me-section mb-0" id="about">
                    <div className="container">
                        <h2 className="page-section-heading text-center text-uppercase text-secondary mb-4">
                            About me
                        </h2>
                        <div className="row">
                            <div className="col-lg-5 ms-auto">
                                <p className="lead">
                                    I'm a computer science student who's passionate about building intricate applications and coding solutions that seem like magic. I started coding back in elementary school, where I tinkered with coding Roblox games with Lua.
                                </p>
                            </div>
                            <div className="col-lg-5 me-auto">
                                <p className="lead">
                                    I’m looking for internships and early-career opportunities where I can contribute, keep learning, and jumpstart my career. In my free time, I love skateboarding, playing video games with friends, and practicing the Piano.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>


                <section className="page-section home-page-section" id="contact">
                    <div className="container">
                        <h2 className="page-section-heading text-center text-uppercase text-secondary mb-4">
                            Contact me
                        </h2>
                        <div className="row justify-content-center">
                            <div className="col-lg-8 col-xl-7">
                                <form id="contactForm" onSubmit={handleContactSubmit}>
                                    <div className="form-floating mb-3">
                                        <input className="form-control" id="name" name="name" type="text" autoComplete="name" maxLength="100" placeholder="Enter your name..." required />
                                        <label htmlFor="name">Full name</label>
                                    </div>
                                    <div className="form-floating mb-3">
                                        <input className="form-control" id="email" name="email" type="email" autoComplete="email" maxLength="254" placeholder="name@example.com" required />
                                        <label htmlFor="email">Email address</label>
                                    </div>
                                    <div className="form-floating mb-3">
                                        <input className="form-control" id="phone" name="phone" type="tel" autoComplete="tel" maxLength="40" placeholder="(123) 456-7890" required />
                                        <label htmlFor="phone">Phone number</label>
                                    </div>
                                    <div className="form-floating mb-3">
                                        <textarea className="form-control" id="message" name="message" maxLength="5000" placeholder="Enter your message here..." style={{ height: '10rem' }} required></textarea>
                                        <label htmlFor="message">Message</label>
                                    </div>
                                    <div className="contact-honeypot" aria-hidden="true">
                                        <label htmlFor="website">Leave this field empty</label>
                                        <input id="website" name="website" type="text" tabIndex="-1" autoComplete="off" />
                                    </div>
                                    {contactStatus.message && (
                                        <p
                                            className={`contact-status contact-status-${contactStatus.type}`}
                                            role={contactStatus.type === 'error' ? 'alert' : 'status'}
                                            aria-live="polite"
                                        >
                                            {contactStatus.message}
                                        </p>
                                    )}
                                    <button className="btn btn-primary btn-xl" id="submitButton" type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? 'Sending…' : 'Send message'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default App;
